import { useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '@/lib/api'
import type { Project, UUID } from '@/types/api'

import { Button, Field, Input, Select, Textarea } from './components/Field'
import { ErrorBanner } from './components/Feedback'
import { LabelSwatch, TypeIcon } from './components/IssueBadges'
import { Modal } from './components/Modal'
import { Popover, PopoverItem } from './components/Popover'
import { ISSUE_TYPES, PRIORITY_LABELS, TASK_PRIORITIES, TYPE_LABELS } from './filters'
import { cn } from './lib/ui'
import type { Issue, IssueType, IssueWriteInput, TaskPriority } from './types'
import { useCreateIssue } from './useIssueMutations'
import { useSprints } from './useSprints'
import { projectRef, useLabels, useRoster } from './useWorkspace'
import { useVoiceInput } from './useVoiceInput'
import { VoiceFieldControl } from './VoiceFieldControl'

export interface CreateIssueDefaults {
  projectKey?: string | null
  columnId?: UUID | null
  sprintId?: UUID | null
  type?: IssueType
  parentId?: UUID | null
}

/**
 * The create dialog, opened by the toolbar button, by a column's inline add and
 * by pressing `c` anywhere on the board.
 *
 * It keeps the form open after a successful create when "Create another" is
 * ticked, which is how a planning session actually goes: five issues in a row,
 * not one.
 */
export function CreateIssueModal({
  open,
  onClose,
  slug,
  projects,
  defaults,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  slug: string | null
  projects: Project[]
  defaults?: CreateIssueDefaults
  onCreated?: (issue: Issue) => void
}) {
  const [projectKey, setProjectKey] = useState<string>('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<IssueType>(defaults?.type ?? 'task')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [assigneeID, setAssigneeID] = useState('')
  const [storyPoints, setStoryPoints] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [sprintID, setSprintID] = useState<string>('')
  const [labelIDs, setLabelIDs] = useState<string[]>([])
  const [createAnother, setCreateAnother] = useState(false)
  const voice = useVoiceInput(open)

  const { members } = useRoster(slug)
  const { data: labels = [] } = useLabels(slug)
  const create = useCreateIssue(slug)
  // useMutation returns a new object on every render, so the reset function is
  // reached through a ref: depending on the mutation itself would wipe the form
  // the moment the submit turned it pending.
  const resetMutation = useRef(create.reset)
  resetMutation.current = create.reset

  const fallbackKey = useMemo(() => {
    if (defaults?.projectKey) return defaults.projectKey
    const preferred = projects.find((project) => project.is_default) ?? projects[0]
    return preferred ? projectRef(preferred) : ''
  }, [defaults?.projectKey, projects])

  // Reset every time the dialog opens so a cancelled draft never leaks into the
  // next one, but keep the caller's defaults (the column that was clicked).
  useEffect(() => {
    if (!open) return
    setProjectKey(fallbackKey)
    setTitle('')
    setDescription('')
    setType(defaults?.type ?? 'task')
    setPriority('medium')
    setAssigneeID('')
    setStoryPoints('')
    setDueDate('')
    setSprintID(defaults?.sprintId ?? '')
    setLabelIDs([])
    resetMutation.current()
  }, [open, fallbackKey, defaults?.type, defaults?.sprintId])

  const { data: sprints = [] } = useSprints(slug, projectKey || null, false)

  const error = create.error
  const fieldError = (name: string) =>
    error instanceof ApiError ? error.fieldError(name) : undefined

  const submit = () => {
    if (!slug || !projectKey || !title.trim() || title.trim().length > 255 || voice.busy || create.isPending) return
    const input: IssueWriteInput = {
      title: title.trim(),
      type,
      priority,
    }
    if (description.trim()) input.description = description.trim()
    if (defaults?.columnId) input.column_id = defaults.columnId
    if (defaults?.parentId) input.parent_id = defaults.parentId
    if (sprintID) input.sprint_id = sprintID
    if (assigneeID) input.assignee_id = Number(assigneeID)
    if (storyPoints.trim()) {
      const points = Number(storyPoints)
      if (Number.isFinite(points)) input.story_points = points
    }
    if (dueDate) input.due_date = dueDate
    if (labelIDs.length > 0) {
      // The API takes label names or ids; ids are unambiguous.
      input.labels = labelIDs
    }

    create.mutate(
      { projectKey, input },
      {
        onSuccess: (issue) => {
          voice.reset()
          onCreated?.(issue)
          if (createAnother) {
            setTitle('')
            setDescription('')
            setStoryPoints('')
          } else {
            onClose()
          }
        },
      },
    )
  }

  const selectedLabels = labels.filter((label) => labelIDs.includes(label.id))
  const handleClose = () => { voice.cancel(); onClose() }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create issue"
      description="Shortcut: press c anywhere on the board."
      size="lg"
      footer={
        <>
          <label className="mr-auto flex items-center gap-2 text-xs text-ink-500">
            <input
              type="checkbox"
              checked={createAnother}
              onChange={(event) => setCreateAnother(event.target.checked)}
              className="size-3.5 rounded border-ink-300 accent-brand-600"
            />
            Create another
          </label>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!title.trim() || title.trim().length > 255 || !projectKey || voice.busy || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        {error && <ErrorBanner error={error} />}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Project" htmlFor="create-project" error={fieldError('project')}>
            <Select
              id="create-project"
              value={projectKey}
              onChange={(event) => setProjectKey(event.target.value)}
              disabled={projects.length <= 1}
            >
              {projects.length === 0 && <option value="">No projects</option>}
              {projects.map((project) => (
                <option key={project.id} value={projectRef(project)}>
                  {project.name}
                  {project.project_key ? ` (${project.project_key})` : ''}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Type" htmlFor="create-type" error={fieldError('type')}>
            <Select
              id="create-type"
              value={type}
              onChange={(event) => setType(event.target.value as IssueType)}
            >
              {ISSUE_TYPES.map((value) => (
                <option key={value} value={value}>
                  {TYPE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Summary" htmlFor="create-title"
          error={title.trim().length > 255 ? 'Keep the summary to 255 characters. Move extra details to the description.' : fieldError('title')}>
          <Input
            id="create-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="What needs to happen?"
            maxLength={255}
            disabled={voice.busy}
            dir="auto"
            autoComplete="off"
          />
          <VoiceFieldControl voice={voice} field="summary" label="Summary" value={title}
            onChange={setTitle} disabled={create.isPending} />
        </Field>

        <Field label="Description" htmlFor="create-description">
          <Textarea
            id="create-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            disabled={voice.busy}
            dir="auto"
            placeholder="Context, acceptance criteria, links…"
          />
          <VoiceFieldControl voice={voice} field="description" label="Description" value={description}
            onChange={setDescription} disabled={create.isPending} />
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Priority" htmlFor="create-priority">
            <Select
              id="create-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as TaskPriority)}
            >
              {TASK_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {PRIORITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Assignee" htmlFor="create-assignee">
            <Select
              id="create-assignee"
              value={assigneeID}
              onChange={(event) => setAssigneeID(event.target.value)}
            >
              <option value="">Unassigned</option>
              {members.map((member) => (
                <option key={member.user_id} value={String(member.user_id)}>
                  {member.user?.name ?? `User ${member.user_id}`}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Story points" htmlFor="create-points" error={fieldError('story_points')}>
            <Input
              id="create-points"
              value={storyPoints}
              onChange={(event) => setStoryPoints(event.target.value)}
              inputMode="decimal"
              placeholder="—"
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Sprint" htmlFor="create-sprint">
            <Select
              id="create-sprint"
              value={sprintID}
              onChange={(event) => setSprintID(event.target.value)}
            >
              <option value="">Backlog</option>
              {sprints.map((sprint) => (
                <option key={sprint.id} value={sprint.id}>
                  {sprint.name}
                  {sprint.status === 'active' ? ' · active' : ''}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Due date" htmlFor="create-due" error={fieldError('due_date')}>
            <Input
              id="create-due"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          </Field>

          <Field label="Labels">
            <Popover
              label="Choose labels"
              trigger={({ toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  className={cn(
                    'flex w-full items-center gap-1 rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-left text-sm',
                    'dark:border-ink-700 dark:bg-ink-950',
                  )}
                >
                  {selectedLabels.length === 0 ? (
                    <span className="text-ink-400">None</span>
                  ) : (
                    <span className="truncate text-xs">
                      {selectedLabels.map((label) => label.name).join(', ')}
                    </span>
                  )}
                </button>
              )}
            >
              {() =>
                labels.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-ink-500">No labels yet.</p>
                ) : (
                  <>
                    {labels.map((label) => (
                      <PopoverItem
                        key={label.id}
                        selected={labelIDs.includes(label.id)}
                        onClick={() =>
                          setLabelIDs((current) =>
                            current.includes(label.id)
                              ? current.filter((id) => id !== label.id)
                              : [...current, label.id],
                          )
                        }
                      >
                        <span className="flex items-center gap-2">
                          <LabelSwatch color={label.color} />
                          {label.name}
                        </span>
                      </PopoverItem>
                    ))}
                  </>
                )
              }
            </Popover>
          </Field>
        </div>

        <p className="flex items-center gap-1.5 text-xs text-ink-500">
          <TypeIcon type={type} />
          New {TYPE_LABELS[type].toLowerCase()} in {projectKey || 'no project'}
        </p>

        {/* Enter submits from any single-line field. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  )
}
